import {
  Component,
  ElementRef,
  inject,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { ApiService } from '../../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { Socket } from 'ngx-socket-io';
import {
  TAGInvItemGroup,
  TAGInvItemType,
  TAGInvTransaction,
} from '../../../../models/taginventory';
import { MessageService } from 'primeng/api';
import { PrinterService } from '../../../printer.service';
import {
  BarcodeScannerSearchField,
  CodeScannedEvent,
} from '../../../components/barcode-scanner-search-field/barcode-scanner-search-field';
import { findInvByCode } from '../inventoryHelper';
import { MatIcon } from '../../../components/mat-icon/mat-icon';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../../components/action-confirmation/action-confirmation-dialog';
import {
  newProgressDialogStep,
  ProgressDialogStep,
  ProgressDialogStepState,
  openProgressDialog,
} from '../../../components/progressDialog/progressDialog';
import { dialogConfig } from '../../../dialogs';
import { ItemEditDialog, ItemEditDialogData } from '../item-edit-dialog/itemEditDialog';

export interface GroupEditDialogData {
  group: TAGInvItemGroup;
  socket: Socket;
}

@Component({
  selector: 'app-internal-group-edit-dialog',
  imports: [
    TableModule,
    SkeletonModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    FloatLabelModule,
    TextareaModule,
    DatePickerModule,
    TranslatePipe,
    ToggleButtonModule,
    BarcodeScannerSearchField,
    MatIcon,
    ProgressSpinnerModule,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './groupEditDialog.html',
  styleUrl: './groupEditDialog.scss',
})
export class GroupEditDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  public api = inject(ApiService);
  public printer = inject(PrinterService);

  @ViewChild(BarcodeScannerSearchField) search: BarcodeScannerSearchField;

  @ViewChildren('instanceElements') instanceElements!: QueryList<ElementRef>;
  @ViewChildren('typeElements') typeElements!: QueryList<ElementRef>;

  group = signal<TAGInvItemGroup>(new TAGInvItemGroup());
  loadingGroup = signal(true);
  itemTypes = signal<TAGInvItemType[]>([]);
  itemTypesCount = signal<Record<string, number>>({});
  transactions = signal<TAGInvTransaction[]>([]);

  collapsedTypes = signal<Record<string, boolean>>({});

  scrollToItemId = signal<string | null>(null);

  editingDisabled = signal(false);
  deletionDisabled = signal(false);

  groupForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
  });

  constructor(
    public dialogRef: DynamicDialogRef<GroupEditDialog>,
    public config: DynamicDialogConfig<GroupEditDialogData>,
  ) {
    config.header = this.translate.translate('internal.inventory.groupEdit.title')();
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.api
        .getInvTransactions()
        .then((invTransactions: TAGInvTransaction[]) => {
          this.transactions.set(invTransactions);

          this.group.set(new TAGInvItemGroup());

          this.editingDisabled.set(!this.api.hasPermission('create_inventory_item'));
          this.deletionDisabled.set(!this.api.hasPermission('delete_inventory_item'));

          if (this.config.data.group) {
            this.group.set(new TAGInvItemGroup(Object.assign({}, this.config.data.group)));
            if (this.group()._id) {
              this.editingDisabled.set(!this.api.hasPermission('edit_inventory_item'));
            }
          }

          this.groupForm.patchValue(this.group());

          if (this.group().deleted) {
            this.editingDisabled.set(true);
            this.deletionDisabled.set(true);
          }

          this.api
            .getInvItems()
            .then((invItems: TAGInvItemType[]) => {
              this.itemTypes.set(
                invItems.filter(
                  (item) => this.group().items.findIndex((itm) => itm.itemid == item._id) != -1,
                ),
              );

              this.updateValues();
              this.loadingGroup.set(false);
            })
            .catch((reason) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.translate('errors.couldNotLoadInventoryItems')(),
                detail: reason.message,
              });

              this.dialogRef.close();
            });
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotLoadInventoryTransactions')(),
            detail: reason.message,
          });
        });
    });
  }

  instanceDeleted(itemid: string, instanceid: string) {
    var item = this.itemTypes().find((itm) => itm._id == itemid);
    if (!item) return false;

    var instance = item.instances.find((inst) => inst.itemid == instanceid);
    if (!instance) return false;

    return item.deleted || instance.deleted;
  }

  instanceBooked(itemid: string, instanceid: string) {
    let trans = this.transactions().filter((t) => t.containsItemInstance(itemid, instanceid));

    for (let t of trans) {
      let tItem = t.findItem(itemid, instanceid);
      if (!tItem.back) return true;
    }

    return false;
  }

  toggleCollapse(typeId: string): void {
    this.collapsedTypes.update((ct) => {
      ct[typeId] = !ct[typeId];
      return Object.assign({}, ct);
    });
  }

  itemFilter(itemid: string) {
    return this.group().items.filter((comb) => comb.itemid == itemid);
  }

  updateValues() {
    this.itemTypes.update((it) => {
      return it.filter(
        (item) => this.group().items.findIndex((itm) => itm.itemid == item._id) != -1,
      );
    });
    this.itemTypesCount.set(
      this.itemTypes().reduce(
        (acc, item) => {
          acc[item._id] = this.group().items.filter((itm) => itm.itemid === item._id).length;
          return acc;
        },
        {} as Record<string, number>,
      ),
    );

    if (this.scrollToItemId) {
      setTimeout(() => this.scrollToItem(), 100); // small delay to ensure DOM updated
    }
  }

  async codeScanned(code: CodeScannedEvent) {
    this.search.busy = true;

    let { item, instance } = await findInvByCode(
      code.code,
      undefined,
      undefined,
      undefined,
      this.api,
    );

    if (item) {
      if (instance) {
        if (
          this.group().items.findIndex(
            (gItem) => gItem.itemid == item._id && gItem.instanceid == instance.itemid,
          ) == -1
        ) {
          this.group.update((g) => {
            let gNew = new TAGInvItemGroup(g.serialize());
            gNew.items.push({ itemid: item._id, instanceid: instance.itemid });
            return gNew;
          });

          if (this.itemTypes().findIndex((itm) => itm._id == item._id) == -1) {
            this.itemTypes.update((it) => {
              it.push(item);
              return [...it];
            });
          }

          this.updateValues();

          this.messageService.add({
            severity: 'success',
            summary: this.translate.translate('internal.inventory.groupEdit.added')(),
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate('internal.inventory.groupEdit.alreadyAdded')(),
          });
        }
      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('internal.inventory.general.noInstanceID')(),
        });
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('internal.inventory.general.couldNotFindItem')(),
      });
    }

    this.search.busy = false;
    this.search.reset();
  }

  printLabel(item: TAGInvItemType, instance: { itemid: string }) {
    if (!this.printer.isConnected())
      this.printer
        .connectToPrinter()
        .then((result) => {
          this.printer.printItemLabels(item, [instance]);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotConnectToPrinter')(),
            detail: reason.message,
          });
        });
    else this.printer.printItemLabels(item, [instance]);
  }

  selectItem(item: TAGInvItemType, selectedInstances?: string[]) {
    const itemEditRef = this.dialogService.open(
      ItemEditDialog,
      dialogConfig<ItemEditDialogData>({
        data: {
          item: item,
          selectedInstances: selectedInstances,
          socket: this.config.data.socket,
        },
      }),
    );

    itemEditRef.onClose.subscribe((result) => {
      this.search?.reset();
    });
  }

  removeItem(itemid: string, instanceid: string) {
    this.group.update((g) => {
      let newG = new TAGInvItemGroup(g.serialize());
      newG.items = newG.items.filter(
        (item) => !(item.itemid == itemid && item.instanceid == instanceid),
      );
      return newG;
    });

    this.itemTypes.update((it) => {
      return it.filter(
        (item) => this.group().items.findIndex((itm) => itm.itemid == item._id) != -1,
      );
    });

    this.updateValues();
  }

  saveGroup(): void {
    if (!this.groupForm.valid) return;

    let updateStep = newProgressDialogStep(
      new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate('internal.inventory.groupEdit.updateProgress')()),
    );

    if (!this.group()._id) {
      updateStep = newProgressDialogStep(
        new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate('internal.inventory.groupEdit.createProgress')()),
      );
    }

    let progressDialog = openProgressDialog(this.dialogService, [updateStep]);

    progressDialog.onChildComponentLoaded.subscribe((pDialog) => {
      updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      let newGroup = this.group();
      newGroup.name = this.groupForm.value.name;

      if (this.group()._id) {
        this.api
          .updateInvItemGroup(newGroup)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(result);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      } else {
        this.api
          .createInvItemGroup(newGroup)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(result);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      }
    });
  }

  deleteGroup() {
    if (!this.group()._id) return;

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.inventory.groupEdit.deleteTitle', {
          group: this.group(),
        })(),
        this.translate.translate('internal.inventory.groupEdit.deleteMessage', {
          group: this.group(),
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.inventory.groupEdit.deleteProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteInvItemGroup(this.group()._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  scrollToItem() {
    if (!this.scrollToItemId()) return;

    // Item Type
    if (this.scrollToItemId().indexOf('-') === -1) {
      const target = this.typeElements
        .toArray()
        .find((el) => el.nativeElement.id === this.scrollToItemId());

      if (target) {
        target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Remove the shaking after 2 seconds
      setTimeout(() => {
        this.scrollToItemId.set(null);
      }, 2000);
    } else {
      //Item Instance
      const target = this.instanceElements
        .toArray()
        .find((el) => el.nativeElement.id === this.scrollToItemId());

      if (target) {
        if (this.collapsedTypes()[this.scrollToItemId().split('-')[0]]) {
          this.collapsedTypes.update((ct) => {
            ct[this.scrollToItemId().split('-')[0]] = false;
            return Object.assign({}, ct);
          });

          //this.cdr.detectChanges();  // Manually trigger change detection
          setTimeout(() => {
            target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        } else {
          target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Remove the shaking after 2 seconds
        setTimeout(() => {
          this.scrollToItemId.set(null);
        }, 2000);
      } else {
        console.error('Element not found', this.scrollToItemId());
      }
    }
  }

  instanceShaking(itemid: string, instanceid: string) {
    return itemid + '-' + instanceid === this.scrollToItemId();
  }

  typeShaking(itemid: string) {
    return itemid === this.scrollToItemId();
  }
}
